import * as types from "../mutation-types";
import merge from "lodash.merge";
import omit from "lodash.omit";
import Vue from "vue";

import { EventBus } from "@/EventBus";

import { AuthService, UserResource } from "@/services";

import * as errors from "@/errors";
import * as events from "@/events";

// initial state
const state = {
  all: {
    user: {},
    userToBeTransfered: {},
    temp: {},
    loading: false,
    loaders: {},
    twoFactorLoading: false,
    isLoggedIn: false,
    isLocked: false,
    twoFactorRequired: false,
    emailRequired: false,
    activationRequired: false
  }
};

// getters
const getters = {
  getUser: state => state.all.user,
  getAvatar: state => {
    return (
      state.all.user.avatars &&
      state.all.user.avatars.default &&
      state.all.user.avatars.default.url
    );
  },
  isLoggedIn: state => state.all.isLoggedIn,
  isLocked: state => state.all.isLocked,
  twoFactorRequired: state => state.all.twoFactorRequired,
  emailRequired: state => state.all.emailRequired,
  activationRequired: state => state.all.activationRequired,
  getUserToBeTransfered: state => state.all.userToBeTransfered,
  getTemp: state => {
    return JSON.parse(JSON.stringify(state.all.temp));
  },
  getLoading: state => state.all.loading,
  getTwoFactorLoading: state => state.all.twoFactorLoading,
  getLoaders: state => state.all.loaders
};

// actions
const actions = {
  getUserData({ commit }) {
    return UserResource.get()
      .then(response => {
        commit(types.SET_IS_LOGGED_IN, true);
        commit(types.TWO_FACTOR_REQUIRED, false);
        commit(types.EMAIL_REQUIRED, false);
        commit(types.RECEIVE_USER, response.body);
      })
      .catch(err => {
        if (err instanceof errors.TwoFactorRequired) {
          commit(types.TWO_FACTOR_REQUIRED, true);
        } else {
          commit(types.TWO_FACTOR_REQUIRED, false);
        }

        if (err instanceof errors.EmailRequired) {
          commit(types.EMAIL_REQUIRED, true);
        } else {
          commit(types.EMAIL_REQUIRED, false);
        }

        commit(types.SET_IS_LOGGED_IN, false);
        commit(types.RECEIVE_USER, {});
      });
  },
  getUserRegistrationData({ commit }) {
    return UserResource.getRegistrationData()
      .then(response => {
        commit(types.RECEIVE_REGISTRATION_DATA, response.body);
      })
      .catch(err => {
        commit(types.RECEIVE_REGISTRATION_DATA, {});
      });
  },
  login({ commit }, data) {
    commit(types.LOADING, true);
    return AuthService.login(data)
      .then(() => {
        commit(types.TWO_FACTOR_REQUIRED, false);
        commit(types.ACTIVATION_REQUIRED, false);
        commit(types.IS_LOCKED, false);
        AuthService.redirect();
      })
      .catch(err => {
        EventBus.$emit(events.CAPTCHA_RESET);

        if (err instanceof errors.TwoFactorRequired) {
          commit(types.TWO_FACTOR_REQUIRED, true);
        } else {
          commit(types.TWO_FACTOR_REQUIRED, false);
        }

        if (err instanceof errors.Locked) {
          commit(types.IS_LOCKED, err.message);
        } else {
          commit(types.IS_LOCKED, false);
        }

        if (err instanceof errors.NotActive) {
          commit(types.ACTIVATION_REQUIRED, true);
        } else {
          commit(types.ACTIVATION_REQUIRED, false);
        }

        commit(types.LOADING, false);
        commit(types.RECEIVE_USER, {});
      });
  },
  setIsLocked({ commit }, value) {
    commit(types.IS_LOCKED, value);
  },
  twoFactor({ commit }, token) {
    commit(types.LOADING_TWO_FACTOR, true);
    return AuthService.twoFactor(token)
      .then(() => {
        commit(types.TWO_FACTOR_REQUIRED, false);
        commit(types.LOADING, true);
        AuthService.redirect();
      })
      .catch(err => {
        if (err instanceof errors.Locked) {
          commit(types.SET_IS_LOGGED_IN, false);
          commit(types.LOADING_TWO_FACTOR, false);
          commit(types.IS_LOCKED, err.message);
        } else {
          commit(types.IS_LOCKED, false);
        }

        if (err instanceof errors.UnauthorizedError) {
          commit(types.SET_IS_LOGGED_IN, false);
          commit(types.LOADING_TWO_FACTOR, false);
        } else {
          commit(types.LOADING_TWO_FACTOR, true);
        }
      });
  },
  disconnectThirdParty({ commit }, provider) {
    commit(types.LOADING, true);
    return UserResource.disconnectThirdParty({
      provider: provider
    })
      .then(() => {
        commit(types.LOADING, false);
        commit(types.UPDATE_THIRD_PARTY_PROVIDER, provider);
      })
      .catch(err => {
        commit(types.LOADING, false);
      });
  },
  loginThirdParty({ commit }, provider) {
    commit(types.LOADING, true);
    return AuthService.loginThirdParty(provider);
  },
  getAccountToBeTransfered({ commit }, id) {
    return UserResource.getAccountToBeTransfered({
      id: id
    }).then(response => {
      commit(types.RECEIVE_USER_TO_BE_TRANSFERED, response.body);
    });
  },
  saveUserAttribute({ commit, state }, data) {
    let attributesObject = {};
    let attributes = Object.keys(data);

    attributes.map(key => {
      attributesObject[key] = true;
    });

    commit(types.LOADING, true);

    commit(
      types.LOADING_ATTRIBUTE_ADD,
      merge(state.all.loaders, attributesObject)
    );

    return UserResource.update(data)
      .then(response => {
        commit(types.LOADING, false);
        commit(types.UPDATED_USER, response.body);
        commit(
          types.LOADING_ATTRIBUTE_REMOVE,
          omit(state.all.loaders, attributes)
        );
      })
      .catch(e => {
        commit(types.LOADING, false);
        commit(
          types.LOADING_ATTRIBUTE_REMOVE,
          omit(state.all.loaders, attributes)
        );
      });
  },
  setUserAvatar({ commit }, data) {
    commit(types.LOADING, true);
    return UserResource.setAvatar(data)
      .then(response => {
        commit(types.LOADING, false);
        commit(types.UPDATED_USER_AVATAR, response.body);
      })
      .catch(e => {
        commit(types.LOADING, false);
      });
  },
  redirectAfterLogin({ commit }) {
    commit(types.LOADING, true);
    AuthService.redirect();
  },
  updateUserAttribute({ commit }, data) {
    commit(types.UPDATE_USER_ATTRIBUTE, data);
  }
};

// mutations
const mutations = {
  [types.LOADING](state, value) {
    Vue.set(state.all, "loading", value);
  },
  [types.LOADING_TWO_FACTOR](state, value) {
    Vue.set(state.all, "twoFactorRequired", value);
  },
  [types.LOADING_ATTRIBUTE_ADD](state, attributes) {
    state.all.loaders = {};
    Vue.set(state.all, "loaders", attributes);
  },
  [types.LOADING_ATTRIBUTE_REMOVE](state, attributes) {
    Vue.set(state.all, "loaders", attributes);
  },
  [types.UPDATE_THIRD_PARTY_PROVIDER](state, provider) {
    Vue.set(state.all.user, provider + "_id", null);
  },
  [types.RECEIVE_USER](state, user) {
    Vue.set(state.all, "user", user || {});
  },
  [types.UPDATED_USER](state, user) {
    Vue.set(state.all, "user", user || {});
  },
  [types.SET_IS_LOGGED_IN](state, val) {
    Vue.set(state.all, "isLoggedIn", val);
  },
  [types.TWO_FACTOR_REQUIRED](state, val) {
    Vue.set(state.all, "twoFactorRequired", val);
  },
  [types.IS_LOCKED](state, val) {
    Vue.set(state.all, "isLocked", val);
  },
  [types.EMAIL_REQUIRED](state, val) {
    Vue.set(state.all, "emailRequired", val);
  },
  [types.ACTIVATION_REQUIRED](state, val) {
    Vue.set(state.all, "activationRequired", val);
  },
  [types.RECEIVE_REGISTRATION_DATA](state, user) {
    Vue.set(state.all, "temp", user || {});
  },
  [types.UPDATE_USER_ATTRIBUTE](state, { attribute, value }) {
    Vue.set(state.all.user, attribute, value);

    if (attribute === "country_code") {
      Vue.set(state.all.user, "state_code", null);
    }
  },
  [types.UPDATED_USER_AVATAR](state, user) {
    Vue.set(state.all, "user", user);
  },
  [types.RECEIVE_USER_TO_BE_TRANSFERED](state, user) {
    Vue.set(state.all, "userToBeTransfered", user);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
